import styled from 'styled-components';
import { Spin } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './Spinner.translations.json';
import { colorPrimary } from '@atlas-frontend/design-tokens';
import { FC, PropsWithChildren, useEffect, useState } from 'react';

const { addTranslations, useTranslation } = i18nNsFactory('last-changed');
addTranslations(translations);

type LoaderProps = {
  isLoading?: boolean;
  message?: string;
  overlay?: boolean;
  delay?: number;
} & PropsWithChildren;

type LoaderInnerProps = {
  message: string;
  overlay?: boolean;
  delay?: number;
};

/**
 * A generic loader component that can be used standalone or as a wrapper.
 *
 * @example
 * // Standalone
 * if (condition) {
 *   return <Loader />;
 * }
 *
 * // Wrapper
 * <Spinner isLoading={condition}>
 *   <MyComponent />
 * </Spinner>
 */
export const Spinner: FC<LoaderProps> = ({
  children,
  isLoading,
  message,
  overlay,
  delay,
}) => {
  const { t } = useTranslation();

  const msg = message ?? t('page-loading');

  if (isLoading === undefined || isLoading) {
    return <LoaderInner overlay={overlay} message={msg} delay={delay} />;
  }

  return children;
};

const LoaderInner: FC<LoaderInnerProps> = ({ message, overlay, delay }) => {
  const [show, setShow] = useState(!delay);

  useEffect(() => {
    if (delay) {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  if (!show) {
    return null;
  }

  return (
    <LoadingContainer $overlay={overlay}>
      <LoadingTextContainer>
        <Spin size="large" />
        <LoadingText>{message}</LoadingText>
      </LoadingTextContainer>
    </LoadingContainer>
  );
};

const LoadingContainer = styled.div<{ $overlay?: boolean }>`
  display: grid;
  place-items: center;
  height: 100%;

  ${({ $overlay }) =>
    $overlay
      ? `
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
  bottom: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.5);
  `
      : ''}
`;

const LoadingTextContainer = styled.div`
  display: grid;
  gap: 0.5em;
`;

const LoadingText = styled.strong`
  color: ${colorPrimary};
`;
